/**
 * RasterColorField — swatch + hex input for Classify / Unique Values rows.
 *
 * The native `<input type="color">` picker only exposes R/G/B on Windows. This
 * field keeps that swatch and adds a `#rrggbb` text box so operators can type
 * or paste hex the same way they would in ArcGIS / QGIS.
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { tokens } from '@/design';
import { normalizeHexColor } from './rasterClassify';

export interface RasterColorFieldProps {
  /** Current colour as `#rrggbb` (case-insensitive). */
  value: string;
  onChange: (nextHex: string) => void;
  /** Accessible name for the swatch (e.g. "Class 1 color"). */
  'aria-label': string;
  /** Accessible name for the hex text field. Defaults to "`aria-label` hex". */
  hexAriaLabel?: string;
  disabled?: boolean;
  'data-testid'?: string;
}

const hexInputStyle: CSSProperties = {
  minHeight: tokens.density.touchMin,
  minWidth: 88,
  width: '100%',
  padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
  background: tokens.color.surface[1],
  color: tokens.color.ink.primary,
  border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
  borderRadius: tokens.radius.input,
  fontFamily: tokens.font.family.mono,
  fontSize: tokens.font.size.sm,
  textTransform: 'uppercase',
};

function toSwatchValue(hex: string): string {
  return normalizeHexColor(hex) ?? '#808080';
}

export function RasterColorField({
  value,
  onChange,
  'aria-label': ariaLabel,
  hexAriaLabel,
  disabled,
  'data-testid': testId,
}: RasterColorFieldProps) {
  const swatch = toSwatchValue(value);
  const [draft, setDraft] = useState(swatch);

  useEffect(() => {
    setDraft(swatch);
  }, [swatch]);

  const commitDraft = (raw: string) => {
    const next = normalizeHexColor(raw);
    if (!next) {
      setDraft(swatch);
      return;
    }
    setDraft(next);
    if (next !== swatch) onChange(next);
  };

  return (
    <div
      data-testid={testId}
      style={{
        display: 'grid',
        gridTemplateColumns: '28px minmax(72px, 1fr)',
        gap: tokens.space[1],
        alignItems: 'center',
        minWidth: 0,
      }}
    >
      <input
        type="color"
        value={swatch}
        aria-label={ariaLabel}
        disabled={disabled}
        onChange={(e) => {
          const next = toSwatchValue(e.currentTarget.value);
          setDraft(next);
          onChange(next);
        }}
        data-testid={testId ? `${testId}-swatch` : undefined}
        style={{
          width: 28,
          height: 28,
          border: 'none',
          padding: 0,
          cursor: disabled ? 'default' : 'pointer',
          background: 'transparent',
        }}
      />
      <input
        type="text"
        value={draft}
        spellCheck={false}
        autoComplete="off"
        disabled={disabled}
        aria-label={hexAriaLabel ?? `${ariaLabel} hex`}
        placeholder="#RRGGBB"
        maxLength={7}
        onChange={(e) => {
          const raw = e.currentTarget.value;
          setDraft(raw);
          const next = normalizeHexColor(raw);
          if (next) onChange(next);
        }}
        onBlur={(e) => commitDraft(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.currentTarget.blur();
          }
        }}
        data-testid={testId ? `${testId}-hex` : undefined}
        style={hexInputStyle}
      />
    </div>
  );
}
